import '/style.css';
import { Map, View } from 'ol';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Icon from 'ol/style/Icon.js';
import { Fill, Stroke, Style, Text } from 'ol/style.js';
import { defaults as defaultInteractions } from 'ol/interaction.js';
import { useGeographic } from 'ol/proj';

useGeographic();

function isMobile() {
  return window.innerWidth <= 768;
}

import Overlay from 'ol/Overlay.js';

const popupContainer = document.getElementById('popup');
const popupContent = document.getElementById('popup-content');
const popupCloser = document.getElementById('popup-closer');

const popupOverlay = new Overlay({
  element: popupContainer,
  positioning: 'bottom-center',
  stopEvent: true,
  autoPan: {
    animation: {
      duration: 300,
      easing: (t) => t * (2 - t), // smooth easeOut
    },
    margin: 16,
  },
});

const markerStyles = {
  ho: new Style({
    image: new Icon({
      src: '/static/images/icons/ho.svg',
      anchor: [0.5, 1],
    }),
  }),

  rp: new Style({
    image: new Icon({
      src: '/static/images/icons/rp.svg',
      scale: 0.7,
      anchor: [0.5, 1],
    }),
  }),

  rg: new Style({
    image: new Icon({
      src: '/static/images/icons/rg.svg',
      scale: 0.75,
      anchor: [0.5, 1],
    }),
  }),

  ot: new Style({
    image: new Icon({
      src: '/static/images/icons/ot.svg',
      scale: 0.7,
      anchor: [0.5, 1],
    }),
  }),

  default: new Style({
    image: new Icon({
      src: '/static/images/icons/ot.svg',
      scale: 0.7,
      anchor: [0.5, 1],
    }),
  }),
};

const textStyle = {
  font: '12px Calibri,sans-serif',
  overflow: true,
  fill: new Fill({
    color: '#FFF',
  }),
  offsetY: 12,
  offsetX: -12,
  stroke: new Stroke({
    color: '#000',
    width: 3,
  }),
};

const labelStyle = new Style({
  text: new Text(textStyle),
});

const polygonStyle = new Style({
  stroke: new Stroke({
    color: '#FFF',
    width: 1,
  }),
  fill: new Fill({
    color: '#CCC',
  }),
});

const polygonHoverStyle = new Style({
  stroke: new Stroke({
    color: '#ff9800',
    width: 2,
  }),
  fill: new Fill({
    color: 'rgba(255, 152, 0, 0.35)',
  }),
});

const style = [polygonStyle];

const countryLayer = new VectorLayer({
  source: new VectorSource({
    url: '/static/data/countries.geojson',
    format: new GeoJSON(),
  }),
  style: function (feature) {
    const name = feature.get('name');
    // labelStyle.getText().setText(feature.get('name'));
    return style;
  },
  declutter: true,
});

const continentIndex = {};
const countryIndex = {};

countryLayer.getSource().on('addfeature', function (e) {
  const feature = e.feature;
  const continent = feature.get('CONTINENT');
  if (continent && !continentIndex[continent]) {
    continentIndex[continent] = [];
  }

  continentIndex[continent].push(feature);

  const country = feature.get('ISO_A3');
  if (country) {
    countryIndex[country] = feature;
  }
});

const centroidLayer = new VectorLayer({
  source: new VectorSource({
    url: '/static/data/centroids.geojson',
    format: new GeoJSON(),
  }),
  style: function (feature) {
    const type = feature.get('type');
    const name = feature.get('location');

    const iconStyle = markerStyles[type] || markerStyles.default;

    labelStyle.getText().setText(name);
    labelStyle.getText().setOffsetX(feature.get('x'));
    labelStyle.getText().setOffsetY(feature.get('y'));

    return [iconStyle, labelStyle];
  },
});

const map = new Map({
  target: 'map',
  layers: [countryLayer, centroidLayer],
  view: new View({
    center: [51.665556, 32.926583],
    zoom: 3.5,
  }),
  controls: [],
  interactions: defaultInteractions({
    doubleClickZoom: false,
    dragAndDrop: false,
    dragPan: true,
    keyboardPan: false,
    keyboardZoom: false,
    mouseWheelZoom: false,
    pointer: false,
    select: false,
  }),
});

map.addOverlay(popupOverlay);

popupCloser.onclick = function (e) {
  e.preventDefault();
  popupOverlay.setPosition(undefined);
  return false;
};

map.on('singleclick', function (evt) {
  let feature = null;

  map.forEachFeatureAtPixel(evt.pixel, function (f) {
    if (f.getGeometry().getType() === 'Point') {
      feature = f;
      return true;
    }
  });

  const popupEl = popupOverlay.getElement();
  if (feature.get('type') && feature.get('type') === 'ho') {
    popupEl.classList.add('large');
  } else {
    popupEl.classList.remove('large');
  }

  if (!feature) return;
  if (!feature.get('address') && !feature.get('phone')) return;

  const coord = feature.getGeometry().getCoordinates();

  let htmlContent = `<h4>${feature.get('name')}</h4>`;

  if (feature.get('address')) {
    htmlContent += `<p>${feature.get('address')}</p>`;
  }

  if (feature.get('phone')) {
    htmlContent += `<p><a href="tel:${feature.get('phone')}">${feature.get('phone')}</a>`;
    if (feature.get('phone2')) {
      htmlContent += `<br><a href="tel:${feature.get('phone2')}">${feature.get('phone2')}</a>`;
    }
    if (feature.get('phone3')) {
      htmlContent += `<br><a href="tel:${feature.get('phone3')}">${feature.get('phone3')}</a>`;
    }
    htmlContent += `</p>`;
  }

  if (feature.get('email')) {
    htmlContent += `<p><a href="mailto:${feature.get('email')}">${feature.get('email')}</a></p>`;
  }

  if (feature.get('address2')) {
    htmlContent += feature.get('address2');
  }

  popupContent.innerHTML = htmlContent;

  if (isMobile()) {
    // 🔹 Mobile: show popup at bottom-center of map
    const view = map.getView();
    const extent = view.calculateExtent(map.getSize());

    const bottomCenter = [(extent[0] + extent[2]) / 2, extent[1]];

    popupOverlay.setPositioning('bottom-center');
    popupOverlay.setPosition(bottomCenter);
  } else {
    // 🔹 Desktop: anchor to marker
    popupOverlay.setPositioning('bottom-center');
    popupOverlay.setPosition(coord);
  }
});

let highlightedContinent = null;
let highlightedCountry = null;

map.on('pointermove', function (evt) {
  if (evt.dragging) return;
  if (window.innerWidth <= 768) return; // disable on mobile

  let hoveredPoint = null;

  map.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
    if (layer === centroidLayer) {
      hoveredPoint = feature;
      return true;
    }
  });

  const hitMarker = map.hasFeatureAtPixel(evt.pixel, {
    layerFilter: (layer) => layer === centroidLayer,
  });

  map.getTargetElement().style.cursor = hitMarker ? 'pointer' : '';

  // Remove previous highlight
  if (highlightedContinent) {
    highlightedContinent.forEach(function (polygon) {
      polygon.setStyle(undefined);
    });
    highlightedContinent = null;
  }

  if (highlightedCountry) {
    highlightedCountry.setStyle(undefined);
    highlightedCountry = null;
  }

  if (!hoveredPoint) return;

  const code = hoveredPoint.get('continent');
  const continentFeature = continentIndex[code];

  if (continentFeature) {
    continentFeature.forEach(function (polygon) {
      polygon.setStyle(polygonHoverStyle);
    });
    highlightedContinent = continentFeature;
  }

  const countryCode = hoveredPoint.get('country');
  const countryFeature = countryIndex[countryCode];

  if (countryFeature) {
    countryFeature.setStyle(polygonHoverStyle);
    highlightedCountry = countryFeature;
  }
});

map.on('pointerdown', function (evt) {
  if (!isMobile()) return;

  const hit = map.hasFeatureAtPixel(evt.pixel);
  if (!hit) {
    popupOverlay.setPosition(undefined);
  }
});

function adjustMarkerScale() {
  const scale = window.innerWidth <= 768 ? 0.9 : 0.7;

  Object.values(markerStyles).forEach((style) => {
    style.getImage().setScale(scale);
  });

  centroidLayer.changed();
}

adjustMarkerScale();
window.addEventListener('resize', adjustMarkerScale);
